"use client";

import React, { FC, useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";
import axios from "axios";

import { Modal } from "@mantine/core";
import Image from "next/image";
import HtmlText from "@/sections/common/HtmlText";
import TButton from "../atoms/buttons/TButton";
import { useQuery } from "@tanstack/react-query";
import { getImageUrl } from "@/utils/media.helper";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import { informationPopUpQuery } from "@/api/information-pop-up";
import { X, CheckCircle } from "react-feather";

import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { usePathname } from "next/navigation";
import TInput from "../atoms/input/TInput";
import { newsLetterSchema } from "@/app/api/validationSchemas";
import { getCookie, CookieName } from "@/utils/cookie.helper";

import { useDisclosure } from "@mantine/hooks";

import GoogleReCaptcha from "@/sections/common/GoogleReCaptcha";
import useGoogleReCaptcha from "@/hooks/useGoogleReCaptcha";
import { handleException, IAxiosError } from "@/utils/exception.helper";

// ----------------------------------------------------------------------------

type INewsLatterSubscriptionPopUpProps = {};

type IDefaultValues = {
  firstName: string;
  lastName: string;
  email: string;
};

const defaultValues: IDefaultValues = {
  firstName: "",
  lastName: "",
  email: "",
};

const NewsLatterSubscriptionPopUp: FC<
  INewsLatterSubscriptionPopUpProps
> = () => {
  const [opened, { open, close }] = useDisclosure(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);

  const {
    reCaptchaRef,
    isReCaptchaAvailable,
    isRecaptchaVarified,
    resetReCaptcha,
  } = useGoogleReCaptcha();

  const path = usePathname();

  const enableQuery = useCallback(() => {
    return !!(
      (path.split("/")[1] === "technical-blogs" ||
        path.split("/")[1] === "leadership-insights") &&
      getCookie(CookieName.MDT_CONSENT) &&
      getCookie(CookieName.LATEST_E_BOOK) &&
      !getCookie(CookieName.NEWSLETTER_SUBSCRIPTION)
    );
  }, [path]);

  const { data, isError } = useQuery({
    ...informationPopUpQuery.getNewsLatterSubscriptionDetail(),
    enabled: enableQuery(),
  });

  const { heading, description, image } = data?.data.attributes ?? {};

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (enableQuery() && !isError) {
      timeout = setTimeout(() => {
        open();
      }, 120 * 1000);
    }

    return () => clearTimeout(timeout);
  }, [enableQuery, isError, open]);

  const onClose = () => {
    close();
    Cookies.set("news-latter-subscription-pop-up", "false", { expires: 365 });
  };

  const { handleSubmit, reset, control } = useForm({
    defaultValues,
    resolver: yupResolver(newsLetterSchema),
  });

  const onSubmit = async (formData: IDefaultValues) => {
    const { success, token } = await isRecaptchaVarified();
    if (!success) return;

    try {
      setIsSubmitting(true);

      // Send email when user subscribe newslatter
      await axios.post("/api/newslatter/send-mail", formData, {
        headers: token ? { "x-recaptcha-token": token } : undefined,
      });

      reset();
      setIsSubscribed(true);
      // Hold the celebratory state for ~3.5s so the user has time to read
      // it, then close. Cookie is set so the popup won't reappear.
      setTimeout(() => {
        onClose();
        // Leave isSubscribed true — Mantine unmounts on close anyway.
      }, 3500);
    } catch (error) {
      handleException(error as IAxiosError);
    } finally {
      setIsSubmitting(false);
      resetReCaptcha();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      classNames={{
        content: "rounded-none",
        body: "bg-secondary-100 p-0",
      }}
      size="auto"
      withCloseButton={false}
      closeOnClickOutside={false}
      zIndex={9999}
    >
      <div className="relative flex h-[506px] flex-col items-center justify-center md:flex-row">
        <span
          onClick={onClose}
          className="absolute right-6 top-5 z-50 cursor-pointer text-gray-500"
        >
          <X className="size-6 text-black-950 lg:size-7" />
        </span>
        {image && (
          <Image
            src={getImageUrl(image)}
            alt={image.name}
            className="absolute bottom-0 left-0 top-0 hidden h-[506px] w-[356px] object-cover md:block"
            height={506}
            width={356}
          />
        )}
        <div className="ml-0 flex max-h-full flex-col items-center gap-4 overflow-y-scroll px-3 py-12 sm:px-6 md:ml-[356px] md:items-start md:py-9 lg:gap-7 lg:px-8">
          {isSubscribed ? (
            // Celebration state — replaces the form in-place. Holds for
            // 3.5s (via setTimeout in onSubmit) before the modal auto-closes.
            <div className="flex w-full max-w-[434px] flex-col items-center gap-4 py-6 text-center md:items-start md:text-left">
              <div className="relative inline-flex h-16 w-16 items-center justify-center rounded-full bg-yellow-400 text-primary-900 shadow-[0_0_36px_rgba(250,204,21,0.45)]">
                <CheckCircle size={34} strokeWidth={2.5} />
              </div>
              <h5 className="font-bricolage-grotesque text-h7 font-medium text-primary-900 lg:text-h6">
                You&apos;re on the list.
              </h5>
              <p className="text-sm text-black-700 md:text-base">
                First Friday briefing lands in your inbox this week. We keep
                it tight — one read, real signal, zero spam.
              </p>
            </div>
          ) : (
            <>
              {heading && (
                <h5 className="text-lg font-normal text-primary-900 lg:text-h6 xl:text-h5">
                  {heading}
                </h5>
              )}
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex w-full flex-col gap-5"
                id="news-letter-pop-up-form"
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <TInput
                    name="firstName"
                    control={control}
                    placeholder="First Name *"
                  />
                  <TInput
                    name="lastName"
                    control={control}
                    placeholder="Last Name *"
                  />
                </div>
                <TInput
                  name="email"
                  control={control}
                  placeholder="Work Email *"
                />

                <div>
                  {/* Google reCAPTCHA component */}
                  {isReCaptchaAvailable && (
                    <GoogleReCaptcha ref={reCaptchaRef} />
                  )}

                  <TButton
                    id="newsletter-pop-up-form-submit-button"
                    type="submit"
                    variant="yellow"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Subscribing…" : "Subscribe"}
                  </TButton>
                </div>
              </form>
              {description && (
                <HtmlText className="!max-w-[434px] text-center prose-p:!text-sm md:text-left">
                  <BlocksRenderer content={description} />
                </HtmlText>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default NewsLatterSubscriptionPopUp;
